import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X, Link2, MessageCircle } from 'lucide-react';
import type { ReceiptPrintData, ReceiptPrintFormat } from '../types';
import { formatReceiptNumber } from '../types';
import { useToast } from '@/hooks/use-toast';
import { WhatsAppMessageDialog } from '@/components/WhatsAppMessageDialog';
import { formatBs } from '@/lib/money';
import { ThermalReceiptPrint } from './ThermalReceiptPrint';
import { LetterReceiptPrint } from './LetterReceiptPrint';

/** Who the receipt is for, so WhatsApp can open their chat with a link to their registration */
export interface ReceiptShare {
  phone: string | null;
  name: string;
  /** Link to send; defaults to the public receipt page */
  url?: string;
}

interface Props {
  data: ReceiptPrintData;
  share?: ReceiptShare;
  /** Admins get the thermal layout first (they print on the thermal printer); they can switch to Carta */
  defaultFormat?: ReceiptPrintFormat;
  onClose: () => void;
}

/**
 * Renders through a portal into document.body. The print rule hides every
 * other top-level element (`body > *:not([data-receipt-root])`); a receipt
 * nested inside #root would be hidden along with it — this already caused a
 * blank print preview once in the tithes work.
 */
export function ReceiptPrintPreview({ data, share, defaultFormat = 'thermal', onClose }: Props) {
  const [format, setFormat] = useState<ReceiptPrintFormat>(defaultFormat);
  const [whatsappOpen, setWhatsappOpen] = useState(false);

  const { toast } = useToast();
  const handlePrint = () => window.print();

  const receiptNumber = formatReceiptNumber(data.number);
  const shareUrl = share?.url ?? data.publicUrl;
  const whatsappMessage = share
    ? `Hola ${share.name.split(' ')[0]}, le enviamos su recibo N° ${receiptNumber} por ${formatBs(data.total)}. Puede verlo y descargarlo aquí: ${shareUrl}`
    : `Recibo N° ${receiptNumber}: ${shareUrl}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.publicUrl);
      toast({ title: 'Enlace copiado' });
    } catch {
      toast({ title: 'No se pudo copiar el enlace', variant: 'destructive' });
    }
  };

  // Escape closes, matching the backdrop click below — the letter sheet is
  // taller than the viewport, so the close button can be scrolled away from.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !whatsappOpen) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, whatsappOpen]);

  return createPortal(
    <div data-receipt-root>
      <div
        data-receipt-overlay
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        // items-start, not items-center: the letter sheet is taller than the
        // viewport, and a centred flex child overflows in both directions —
        // the toolbar above it ends up above the scroll origin, unreachable.
        className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/60 py-8"
      >
        <div
          // The wrapper is as wide as the sheet, so the strip beside the
          // narrower toolbar is still "outside" from the user's point of view.
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          className="flex flex-col items-center gap-4"
        >
          <div
            data-receipt-chrome
            className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-lg bg-card px-4 py-3 shadow-lg"
          >
            <span className="text-sm text-muted-foreground">Vista previa del recibo</span>

            <div className="flex overflow-hidden rounded-md border border-border text-sm">
              <button
                onClick={() => setFormat('thermal')}
                className={`px-3 py-1.5 transition-colors ${format === 'thermal' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
              >
                Térmico
              </button>
              <button
                onClick={() => setFormat('letter')}
                className={`px-3 py-1.5 transition-colors ${format === 'letter' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
              >
                Carta
              </button>
            </div>

            <button
              onClick={handlePrint}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Printer className="size-4" />
              Imprimir
            </button>

            <button
              onClick={handleCopyLink}
              aria-label="Copiar enlace público"
              title="Copiar enlace público"
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
            >
              <Link2 className="size-4" />
              <span className="max-sm:hidden">Copiar enlace</span>
            </button>

            <button
              onClick={() => setWhatsappOpen(true)}
              aria-label="Compartir por WhatsApp"
              title="Compartir por WhatsApp"
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
            >
              <MessageCircle className="size-4" />
              <span className="max-sm:hidden">WhatsApp</span>
            </button>

            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
            >
              <X className="size-4" />
              <span className="max-sm:hidden">Cerrar</span>
            </button>
          </div>

          <div data-receipt-sheet className="shadow-2xl">
            {format === 'thermal'
              ? <ThermalReceiptPrint data={data} />
              : <LetterReceiptPrint data={data} />}
          </div>
        </div>
      </div>

      <WhatsAppMessageDialog
        open={whatsappOpen}
        onOpenChange={setWhatsappOpen}
        recipientName={share?.name}
        phone={share?.phone}
        defaultMessage={whatsappMessage}
      />

      <style>{`
        @media print {
          @page { size: ${format === 'thermal' ? '80mm auto' : 'letter'}; margin: 0; }
          body > *:not([data-receipt-root]) { display: none !important; }
          [data-receipt-overlay] {
            position: static !important;
            display: block !important;
            background: transparent !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          [data-receipt-chrome] { display: none !important; }
          [data-receipt-sheet] { box-shadow: none !important; }
        }
      `}</style>
    </div>,
    document.body,
  );
}
